import React, { useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import useCollapse from 'react-collapsed';
import get from 'lodash/get';
import { Button } from '@alefeducation/ui-elements-library-core';
import Container from '../common/Container/Container';
import ChevronDown from '../common/Icons/ChevronDown';
import { percentage } from '../../utils/unit-converter';
import getLocaleDate from '../../utils/getLocaleDate';
import { rtl } from '../../utils/language';
import {
  Grade,
  InfoWrapper,
} from '../common/AssignmentPageLayout/AssignmentPageLayout';
import StarsAward from '../common/StarsAward/StarsAward';
import AssignmentContext from '../common/AssignmentContext/AssignmentContext';
import { isSubmissionRequired } from './helpers';
import useSubmissionControlButtons from './useSubmissionControlButtons';
import { useAssignmentAttachmentState } from './AssignmentAttachmentContext';
import NewAttachment from './NewAttachment/Attachment';

export const Title = styled.div`
  font-family: var(--font-family-signika);
  font-size: 24px;
  font-weight: 700;
  line-height: 32px;
  margin-bottom: 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${rtl`
    font-family: var(--font-family-signika);
  `};
`;

export const Label = styled.div`
  font-family: var(--font-family-signika) !important;
  font-size: 16px !important;
  font-weight: 400 !important;
  line-height: 20px !important;

  ${rtl`
    font-family: var(--font-family-signika) !important;
  `};
`;

export const Info = styled.div`
  font-family: var(--font-family-signika) !important;
  font-size: 16px !important;
  font-weight: 300 !important;
  line-height: 20px !important;

  ${rtl`
    font-family: var(--font-family-signika) !important;
  `};
`;

export const ButtonsWrapper = styled.div`
  flex-grow: 1;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;

  button {
    width: fit-content;
  }
`;
const DescriptionWrapper = styled.div`
  margin-bottom: 32px;

  > div:first-child {
    display: flex;
  }

  > div:last-child {
    font-family: var(--font-family-signika);
    font-size: 16px;
    font-weight: 300;
    line-height: 1.5;
    margin-top: 5px;
  }

  svg {
    color: #4a4a4a;
    cursor: pointer;
    margin-left: 5px;
    margin-right: 5px;
    transform: rotateX(180deg);
  }
`;

const ReturnedScore = styled.span`
  font-weight: 600;
`;

const Feedback = styled.div`
  header {
    font-family: var(--font-family-signika);
    font-weight: bold;
    font-size: 18px;

    ${rtl`
      font-family: Tajawal;
    `};
  }

  p {
    font-family: var(--font-family-signika);

    font-size: 16px;
    font-weight: 300;
    line-height: 1.5;
  }
`;

// eslint-disable-next-line react/prop-types
const ScoreBreakdown = ({ score, maxScore }) => {
  const scoreMap = {
    score: '--',
    percentage: '',
  };
  if (score) {
    scoreMap.score = (
      <ReturnedScore data-testid="assignment-panel-returned-score">
        {score}
      </ReturnedScore>
    );
    scoreMap.percentage = `- ${percentage(score, maxScore)}%`;
  }
  return (
    <span dir="ltr">
      {scoreMap.score}/{maxScore}
      {scoreMap.percentage}
    </span>
  );
};

const NewAssignmentPanel = ({
  assignment,
  submission,
  isUpdatingCommentCompleted,
  onSaveAction,
  onSubmitAction,
  isSubmitted,
  shouldHideButtons,
  onButtonsStateChange,
}) => {
  const { t, i18n } = useTranslation();
  const {
    assignmentData: { assignmentType },
  } = useContext(AssignmentContext);
  const assignmentTypeForPendoClass = assignmentType
    .split('_')
    .join('-')
    .toLowerCase();
  const toggleDescriptionRef = useRef();
  const {
    conversionStatus,
    attachment,
    uploadStatus,
  } = useAssignmentAttachmentState();

  const {
    isSubmitButtonDisabled,
    isSaveButtonDisabled,
  } = useSubmissionControlButtons({
    assignment,
    attachment,
    conversionStatus,
    uploadStatus,
    isUpdatingCommentCompleted,
  });

  useEffect(
    () => {
      if (shouldHideButtons)
        onButtonsStateChange({
          isSubmitButtonDisabled,
          isSaveButtonDisabled,
          isSubmitted,
          requiresSubmission:
            assignment.commentRequired || assignment.attachmentRequired,
        });
    },
    [isSaveButtonDisabled, isSubmitButtonDisabled, isSubmitted],
  );

  const { getCollapseProps, toggleOpen } = useCollapse({
    defaultOpen: true,
    expandStyles: {
      transitionDuration: '300ms',
      transitionTimingFunction: 'linear',
    },
    collapseStyles: {
      transitionDuration: '300ms',
      transitionTimingFunction: 'linear',
    },
  });

  const getCollapsePropsa = getCollapseProps();

  const toggleDescription = event => {
    event.preventDefault();
    event.stopPropagation();
    const svg = toggleDescriptionRef.current.children[0];
    svg.classList.toggle('assignmentSubmission__collapsed-chevron');
    toggleOpen();
  };

  const isAttachment = () => assignment.attachment !== null;

  const description = assignment.description.replace(/\n/g, '<br />');

  const dueDate = getLocaleDate({
    date: assignment.dueOn,
    language: i18n.language,
  });

  const submissionComment = get(submission, 'response.comment');

  // eslint-disable-next-line no-nested-ternary
  const submitButtonTip = isSubmitButtonDisabled
    ? assignment.attachmentRequired
      ? t('You need to upload a file before you can submit your assignment.')
      : t('You need to add a comment before you can submit your assignment')
    : '';
  // eslint-disable-next-line no-nested-ternary
  const saveButtonTip = isSaveButtonDisabled
    ? assignment.attachmentRequired
      ? t(
          'You need to add a comment and/or upload a file before you can save your assignment',
        )
      : t('You need to add a comment before you can save your assignment')
    : '';

  const shouldShowFeedback =
    get(submission, 'status') === 'RETURNED' && submissionComment;
  const shouldShowGrade = get(submission, 'status') === 'RETURNED';

  return (
    <Container className="assignmentSubmission-container assignmentSubmission-container__column">
      <Title>{assignment.title}</Title>
      <InfoWrapper>
        {assignment.dueOn && (
          <div>
            <Label>{t('Due By')}</Label>
            <Info>{dueDate}</Info>
          </div>
        )}
        {assignment.gradeable && (
          <>
            <div data-testid="grade-container">
              <Label>{t('Grade')}</Label>
              <Grade>
                <ScoreBreakdown
                  score={
                    shouldShowGrade
                      ? get(submission, 'response.score')
                      : undefined
                  }
                  maxScore={assignment.maxScore}
                />
              </Grade>
            </div>
            {assignment.maxStars && (
              <StarsAward
                max={assignment.maxStars}
                earned={get(submission, 'response.stars', 0)}
              />
            )}
          </>
        )}
      </InfoWrapper>
      <DescriptionWrapper>
        <div>
          <Label>{t('Assignment Description')}</Label>
          <span
            data-testid="toggleDescriptionButton"
            ref={toggleDescriptionRef}
            onClick={toggleDescription}
          >
            <ChevronDown size={12} />
          </span>
        </div>
        <div
          {...getCollapsePropsa}
          dangerouslySetInnerHTML={{ __html: description }}
          data-testid="assignmentDescription"
        />
      </DescriptionWrapper>
      {isAttachment() && <NewAttachment attachment={assignment.attachment} />}
      {shouldShowFeedback ? (
        <Feedback
          className="usprivacy"
          data-testid="assignment-panel-teacher-feedback"
        >
          <header>{t('Teacher Feedback')}</header>
          <p>{submissionComment}</p>
        </Feedback>
      ) : (
        <ButtonsWrapper>
          {!shouldHideButtons &&
            isSubmissionRequired(assignment) &&
            !isSubmitted && (
              <>
                <span data-tip={saveButtonTip}>
                  <Button
                    variant={'secondary'}
                    size={'lg'}
                    data-testid="saveButton"
                    disabled={isSaveButtonDisabled}
                    onClick={onSaveAction}
                    className={`data-analytcis-${assignmentTypeForPendoClass}-student-assignment-submission-page-save`}
                  >
                    {t('Save')}
                  </Button>
                </span>
                <span data-tip={submitButtonTip}>
                  <Button
                    variant={'primary'}
                    size={'lg'}
                    data-testid="submitButton"
                    disabled={isSubmitButtonDisabled}
                    onClick={onSubmitAction}
                    style={{ whiteSpace: 'nowrap' }}
                    className={`data-analytcis-${assignmentTypeForPendoClass}-student-assignment-submission-page-submit`}
                  >
                    {t('Submit Assignment')}
                  </Button>
                </span>
              </>
            )}
        </ButtonsWrapper>
      )}
    </Container>
  );
};

NewAssignmentPanel.propTypes = {
  assignment: PropTypes.object.isRequired,
  submission: PropTypes.object.isRequired,
  isUpdatingCommentCompleted: PropTypes.bool.isRequired,
  isSubmitted: PropTypes.bool.isRequired,
  onSaveAction: PropTypes.func.isRequired,
  onSubmitAction: PropTypes.func.isRequired,
  shouldHideButtons: PropTypes.bool.isRequired,
  onButtonsStateChange: PropTypes.func.isRequired,
};

export default NewAssignmentPanel;
